import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { subscriptionKey } from '@dashboard/lib/queryKeys';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import PaymentsSubscriptionsDetail from './scenes/PaymentsSubscriptionsDetail';
import PaymentsSubscriptionsDetailPayments from './scenes/PaymentsSubscriptionsDetailPayments';
import SubscriptionsDetailEmailLogs from './scenes/SubscriptionsDetailEmailLogs';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderNav from '@dashboard/components/CardHeaderNav';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import ProductPrice from '@dashboard/components/ProductPrice';
import Spinner from '@dashboard/components/Spinner';
import SubscriptionStatusBadge from '@/dashboard/components/SubscriptionStatusBadge/SubscriptionStatusBadge';
import isRecoveredAbandonment from '@/dashboard/services/abandonment/isRecoveredAbandonment';
import isAbandonedAbandonment from '@/dashboard/services/abandonment/isAbandonedAbandonment';
import Tooltip from '@/dashboard/components/Tooltip';
import { formatDateTime } from '@/dashboard/lib/format-date';
import StatusBadge from '@/dashboard/components/StatusBadge';
import Dash from '@/dashboard/components/Dash';

const getTabs = ({ subscriptionId, subscription, refetch }) => [
  {
    name: 'Subscription',
    href: `/orders/subscriptions/${subscriptionId}`,
    render: props => (
      <PaymentsSubscriptionsDetail
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
        refetch={refetch}
      />
    ),
  },
  {
    name: 'Emails',
    href: `/orders/subscriptions/${subscriptionId}/emails`,
    render: props => (
      <SubscriptionsDetailEmailLogs
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
      />
    ),
  },
  {
    name: 'Payments',
    href: `/orders/subscriptions/${subscriptionId}/payments`,
    render: props => (
      <PaymentsSubscriptionsDetailPayments
        {...props}
        subscriptionId={subscriptionId}
        subscription={subscription}
      />
    ),
  },
];

const PaymentsSubscriptionsDetailOverview = () => {
  const { token, sellerId } = useAuth();
  const {
    params: { subscriptionId },
  } = useRouteMatch();

  const {
    data: subscription,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: subscriptionKey(subscriptionId),
    queryFn: () => fetcher(`/sellers/${sellerId}/subscriptions/${subscriptionId}`, token),
  });

  if (isLoading) {
    return (
      <Card variant="root">
        <CardContent variant="root">
          <Spinner />
        </CardContent>
      </Card>
    );
  }

  if (!subscription || error) {
    return (
      <Card variant="root">
        <CardContent variant="root">Something went wrong</CardContent>
      </Card>
    );
  }

  const tabs = getTabs({ subscriptionId, subscription, refetch: refetch });

  const isRecovered = isRecoveredAbandonment(subscription);
  const isAbandoned = isAbandonedAbandonment(subscription);
  return (
    <Card variant="root">
      <CardHeader variant="root">
        <div className="space-y-1 w-full">
          <CardHeaderTitle variant="root">{subscription.page?.name || <Dash />}</CardHeaderTitle>

          <ProductPrice>
            <ProductPrice.Price>
              {getPriceLabel({
                amount: subscription.amount,
                currency: subscription.currency,
                interval: subscription.interval,
                intervalCount: subscription.intervalCount,
                type: 'subscription',
              })}
            </ProductPrice.Price>

            <div className="flex flex-row gap-1">
              {isRecovered ? (
                <>
                  <Tooltip label={`Recovered on ${formatDateTime(subscription.recoveredAt)}`}>
                    <StatusBadge status="recovered" label="Recovered" withInfoIcon />
                  </Tooltip>

                  <SubscriptionStatusBadge
                    status={subscription.status}
                    livemode={subscription.livemode}
                    error={subscription.paymentError}
                  />
                </>
              ) : isAbandoned ? (
                <>
                  <SubscriptionStatusBadge status="abandoned" livemode={subscription.livemode} />
                  <SubscriptionStatusBadge
                    status={subscription.status}
                    livemode={subscription.livemode}
                    error={subscription.paymentError}
                  />
                </>
              ) : (
                <SubscriptionStatusBadge
                  status={subscription.status}
                  livemode={subscription.livemode}
                  error={subscription.paymentError}
                />
              )}
            </div>
          </ProductPrice>
        </div>

        <CardHeaderNav.Container tabs={tabs}>
          {({ currentTab }) =>
            tabs.map(tab => (
              <CardHeaderNav.Tab key={tab.name} tab={tab} current={currentTab.href === tab.href}>
                {tab.name}
              </CardHeaderNav.Tab>
            ))
          }
        </CardHeaderNav.Container>
      </CardHeader>

      <CardContent variant="compact">
        <Switch>
          {tabs.map(tab => (
            <Route key={tab.href} path={tab.href} render={tab.render} exact />
          ))}
        </Switch>
      </CardContent>
    </Card>
  );
};

export default PaymentsSubscriptionsDetailOverview;
